import QuickReplies from 'components/QuickReplies'
import { ContentState, convertToRaw, EditorState, getDefaultKeyBinding, Modifier } from 'draft-js'
import useChatConnections from 'hooks/redux/useChatConnections'
import useContact from 'hooks/redux/useContact'
import useHasFeature from 'hooks/useHasFeature'
import { draftToMarkdown } from 'markdown-draft-js'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SyntheticKeyboardEvent } from 'react-draft-wysiwyg'
import { useDispatch } from 'react-redux'
import { ClipLoader } from 'react-spinners'
import {
    chatSendTypingEvent,
    clearUnread,
    sendChatMessage,
    setChatReplyText,
    updateConnectionEditorContent,
    updateSocialEditorContent,
} from 'store/chat/chat.actions'
import { getReplacedQuickReplayText } from 'utils/getReplacedQuickReplayText'

import { Button, WYSIWYG } from '@missionlabs/smartagent-app-components'

import attachmentIcon from './attachment.svg'
import SocialChatReplier from './SocialChatReplier'
import useSendAttachment from './useSendAttachment'
import useSocialChatReplyFunctionality from './useSocialChatReplyFunctionality'

interface Props {
    isExpanded?: boolean
}

export const CHAT_ATTACHMENT_REGEX =
    /@SA_IMAGE\[([[\w.\-_#@!£$%|^"':;+={}?\(\)\[\] ]+~[\w\\\/]+~\w+(.*)+\])+/gm

const ChatReplier: React.FC<Props> = ({ isExpanded }) => {
    const [isShowWarningMessage, setIsShowWarningMessage] = useState<boolean>(false)
    const [isQuickReplyOn, setIsQuickReplyOn] = useState<boolean>(false)
    const [messageSent, setMessageSent] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const dispatch = useDispatch()
    const hasFeature = useHasFeature()

    const chat = useChatConnections('selected')

    const content = useRef<string>('')
    const currentChatId = useRef<string>('')

    if (!chat) return null

    const { currentEditorContent, id: chatId, currentSocialEditorContent } = chat

    useEffect(() => {
        const content = currentEditorContent.getCurrentContent()
        if (loading && !content.hasText() && chat?.attachments?.files.length === 0)
            setLoading(false)
    }, [chat?.attachments?.files, loading, currentEditorContent])

    useEffect(() => {
        currentChatId.current = chat.id
    }, [chat])

    const {
        inputRef,
        handleUpload,
        dragEvents,
        uploadStagedAttachments,
        files,
        deleteFile,
        attachmentEnabled,
    } = useSendAttachment(currentEditorContent)
    const contact = useContact()
    const dFile = (i: number, fileName: string, editorState: EditorState) => {
        deleteFile(i, fileName, content.current || '', currentChatId.current)
    }

    useEffect(() => {
        if (contact) {
            const replyText = currentEditorContent.getCurrentContent().getPlainText()
            const { variablesWithBrackets, variablesEqualToAttributes } =
                getReplacedQuickReplayText(replyText, contact)
            const isAllVariablesEqualToAttributes = variablesEqualToAttributes.every((el) => el)
            const isShowWarning =
                isQuickReplyOn &&
                !isShowWarningMessage &&
                variablesWithBrackets.length &&
                !isAllVariablesEqualToAttributes
            isShowWarning && setIsShowWarningMessage(true)
        }
        onEditorStateChange(currentEditorContent)
    }, [currentEditorContent?.getCurrentContent()?.getPlainText()])

    useEffect(() => {
        if (chat?.reply?.text && contact) {
            const replyText = chat?.reply?.text
            const currentContent = currentEditorContent?.getCurrentContent().getPlainText('\u0001')
            const { textReplacedWithVariable } = getReplacedQuickReplayText(replyText, contact)
            const replacedReplyText = textReplacedWithVariable || replyText

            if (chat?.socialMediaPlatform && chat.socialMediaPlatform !== 'WHATSAPP') {
                dispatch(
                    updateSocialEditorContent({
                        id: chatId,
                        content:
                            currentSocialEditorContent.length === 0
                                ? replacedReplyText
                                : `${currentSocialEditorContent} ${replacedReplyText}`,
                    }),
                )
            } else {
                onEditorStateChange(
                    EditorState.createWithContent(
                        currentContent?.length === 0
                            ? ContentState.createFromText(replacedReplyText)
                            : ContentState.createFromText(`${currentContent} ${replacedReplyText}`),
                    ),
                )
            }
        }
    }, [chat?.reply?.text])

    const sendMessage = useCallback(async () => {
        setLoading(true)
        const content = currentEditorContent.getCurrentContent()

        const rawObject = convertToRaw(content)

        // Replace actual italic style markdown with * instead of _, to prevent accidental italic formatting when using underscores.
        // Set `escapeMarkdownCharacters` false to prevent strange escaping behaviour.
        // (e.g. "_test_ _test_" would become markdown "_test_ \_test\_")
        const markdown = draftToMarkdown(rawObject, {
            preserveNewlines: true,
            escapeMarkdownCharacters: false,
            styleItems: {
                ITALIC: {
                    open: () => '*',
                    close: () => '*'
                }
            }
        })

        // the following lines are a workaround to get the react markdown component to render empty line breaks as the draftToMarkdown doesn't seem to handle this natively.
        let constructedString = ``
        let lastLineWasList = false

        markdown.split('\n').forEach((x, i) => {
            if (x) {
                constructedString += `${x}\n`
            } else {
                constructedString += `\n${lastLineWasList ? '' : '  '}\n`
            }
            // there appears to be a bug in the react-markdown where lists are not exited. the lastLineWasList variable mitigates for that and ensures correct styling is maintained.
            lastLineWasList =
                x?.startsWith('- ') || (!isNaN(Number(x?.[0])) && x[1] === '.' && x[2] === ' ')
        })
        if (constructedString.length > 1024 && chat.socialMediaPlatform === 'WHATSAPP') return

        if (files?.length) {
            inputRef?.current && (inputRef.current.value = '')
            constructedString = constructedString.replaceAll(CHAT_ATTACHMENT_REGEX, '')
            uploadStagedAttachments(constructedString)
        }
        dispatch(sendChatMessage({ id: chat!.id, msg: constructedString, clearEditor: true }))
        setMessageSent(true)
        dispatch(clearUnread(chat.id))
        setTimeout(() => {
            setReply({ isProbablyJson: false, text: '' }), 50
        })
        setIsShowWarningMessage(false)
    }, [files, currentEditorContent, loading])

    const setReply = useCallback(
        (args: { text: string; isProbablyJson: boolean }) => {
            dispatch(
                setChatReplyText({
                    id: chat!.id,
                    text: args.text,
                    isProbablyJson: args.isProbablyJson,
                }),
            )
        },
        [dispatch, chat],
    )

    const shouldDisableSendButton = useMemo(() => {
        return (
            chat.socialMediaPlatform === 'WHATSAPP' &&
            currentEditorContent?.getCurrentContent()?.getPlainText('\u0001')?.length > 1023
        )
    }, [chat.socialMediaPlatform, chat.currentEditorContent])

    const onEditorStateChange = (editorState: EditorState) => {
        content.current = draftToMarkdown(convertToRaw(editorState.getCurrentContent()))
        dispatch(updateConnectionEditorContent({ id: chatId, content: editorState }))
        setIsShowWarningMessage(false)
    }

    useEffect(() => {
        document.getElementsByClassName('sa-wysiwyg-toolbar')[0]?.setAttribute('tabindex', '0')
        // document.getElementsByClassName('sa-wysiwyg-toolbar__quick-replies')[0].setAttribute('tabindex', '0')
        document.getElementsByClassName('sa-wysiwyg-send')[0]?.setAttribute('tabindex', '0')
        const buttons = document.getElementsByClassName('rdw-option-wrapper')
        for (let i = 0; i < buttons.length; i++) {
            buttons[i]?.setAttribute('tabindex', '0')
        }
    }, [])

    const toolbarButtons = useMemo(() => {
        const buttons: React.ReactElement[] = []

        if (attachmentEnabled) {
            buttons.push(
                <Button
                    className="rdw-option-wrapper"
                    key={0}
                    title="Add attachment"
                    onClick={() => inputRef?.current?.click()}
                    disabled={loading && chat?.attachments?.files.length > 0}
                >
                    <img src={attachmentIcon} alt="" />
                    <input ref={inputRef} multiple type="file" hidden onChange={handleUpload} />
                </Button>,
            )
        }
        return buttons.concat([
            <Button
                onClick={sendMessage}
                disabled={shouldDisableSendButton || loading}
                key={1}
                className="sa-wysiwyg-send"
            >
                {loading ? <ClipLoader size={25} color="#9fabb8" /> : 'Send'}
            </Button>,
            <QuickReplies
                key={2}
                channel="Chat"
                analyticsEnabled={true}
                messageSent={messageSent}
                qrChannel={chat.channel || chat.socialMediaPlatform || 'WEBCHAT'}
                setMessageSent={setMessageSent}
                setIsQuickReplyOn={setIsQuickReplyOn}
            />,
        ])
    }, [attachmentEnabled, sendMessage, loading, shouldDisableSendButton])

    /**
     * The `keyBindingFn` function handles key events and returns specific key
     * bindings based on the event.
     * @param {SyntheticKeyboardEvent} e - The parameter `e`is a SyntheticKeyboardEvent,
     * which represents a keyboard event in a React application. It contains
     * information about the keyboard event such as the keyCode, shiftKey, ctrlKey, and metaKey
     * properties that can be used to determine the key combination
     * @returns The keyBindingFn function returns different key bindings based on the conditions
     * specified in the code snippet:
     */
    const keyBindingFn = (e: SyntheticKeyboardEvent) => {
        if (e.keyCode === 9) return getDefaultKeyBinding(e)

        if (e.keyCode === 13 && e.shiftKey) {
            return 'sa-send-msg'
        }

        if ((e.ctrlKey || e.metaKey) && e.keyCode === 88) {
            return 'cut'
        }

        dispatch(chatSendTypingEvent(chatId))
        // Clicks the editor after a key bind so that the selection is correct after sending message
        ;(document.getElementsByClassName('DraftEditor-root')[0] as HTMLElement)?.click()
        return getDefaultKeyBinding(e)
    }

    /**
     * The `handleKeyCommand` function handles custom commands for editing content in an editor.
     * @param {string} command - The `command` parameter represents the key command that is being triggered by the user.
     * @param {EditorState} editorState - The `editorState` parameter represents the current state of the editor.
     */
    const handleKeyCommand = (command: string, editorState: EditorState) => {
        if (command === 'sa-send-msg') {
            sendMessage()
        }

        // handling cut command
        if (command === 'cut') {
            try {
                // forcing state update on draft.js editor
                selectAndForceEditorUpdate(editorState)
            } catch (error) {
                console.error('Error handling cut operation:', error)
            }
        }
    }

    const { smsIsTooLongToSend } = useSocialChatReplyFunctionality(currentSocialEditorContent)

    const onSocialSubmit = () => {
        if (!currentSocialEditorContent || smsIsTooLongToSend) return
        dispatch(
            sendChatMessage({ id: chat!.id, msg: currentSocialEditorContent, clearEditor: true }),
        )
        dispatch(updateSocialEditorContent({ id: chatId, content: '' }))
        dispatch(setChatReplyText({ id: chat!.id, text: '', isProbablyJson: false }))
        setMessageSent(true)
        dispatch(clearUnread(chat.id))
    }
    const wysiwygProps = {
        isShowWarningMessage,
        setIsShowWarningMessage,
        currentEditorContent,
        onEditorStateChange,
        toolbarButtons,
        files,
        dFile,
        keyBindingFn,
        handleKeyCommand,
    }

    const setMessage = useCallback(
        (message: string) => {
            dispatch(updateSocialEditorContent({ id: chatId, content: message }))
        },
        [chatId],
    )

    /**
     * The function removes the selected text in an editor and forces an update to reflect the change.
     * @param {EditorState} editorState - `editorState` is an object that represents the current state.
     */
    const selectAndForceEditorUpdate = (editorState: EditorState) => {
        const selection = editorState.getSelection()

        if (!selection.isCollapsed()) {
            const contentState = editorState.getCurrentContent()
            const newContentState = Modifier.removeRange(contentState, selection, 'forward')

            const newEditorState = EditorState.push(editorState, newContentState, 'remove-range')

            onEditorStateChange(
                EditorState.forceSelection(newEditorState, newContentState.getSelectionAfter()),
            )
        }
    }

    useEffect(() => {
        const handleCut = (e: ClipboardEvent) => {
            // preventing default browser cut behavior
            e.preventDefault()

            // forcing state update on draft.js editor
            selectAndForceEditorUpdate(currentEditorContent)
        }

        const editorElement = document.getElementsByClassName('DraftEditor-root')[0] as HTMLElement
        editorElement?.addEventListener('cut', handleCut)

        return () => editorElement?.removeEventListener('cut', handleCut)
    }, [currentEditorContent])

    return (
        <div 
            {...(isExpanded && { className: 'sa-social-reply-wrapper' })}
            {...dragEvents}
        >
            {chat.socialMediaPlatform ? (
                <div className="sa-social-media-replier">
                    <SocialChatReplier
                        aria-label="Quick reply button"
                        message={currentSocialEditorContent}
                        setMessage={setMessage}
                        onSubmit={onSocialSubmit}
                        socialMediaPlatform={chat.socialMediaPlatform}
                        messageSent={messageSent}
                        setMessageSent={setMessageSent}
                        setIsQuickReplyOn={setIsQuickReplyOn}
                        className="sa-social-media-replier-input"
                        wysiwygProps={wysiwygProps}
                    />
                </div>
            ) : (
                <WYSIWYG
                    isShowWarningMessage={isShowWarningMessage}
                    setIsShowWarningMessage={setIsShowWarningMessage}
                    editorState={currentEditorContent}
                    onEditorStateChange={onEditorStateChange}
                    placeholder="Enter message..."
                    toolbarButtons={toolbarButtons}
                    files={files}
                    onDeleteFile={dFile}
                    keyBindingFn={keyBindingFn}
                    handleKeyCommand={handleKeyCommand}
                    {...(isExpanded && { wysiwygClassName: 'sa-wysiwyg sa-wysiwyg-expanded' })}
                />
            )}
        </div>
    )
}

export default ChatReplier
